import { useEffect, useRef, useState } from 'react'

import type { EditHistoryState, Operation, ReplayResult } from '@fit-editor/fit-core'

import { currentActivityAsync, previewOperationAsync } from '@/worker/fit-client'

const DEBOUNCE_MS = 300

export interface OperationPreviewState {
  /** The selected activity's state *before* `operation` -- fetched once per `history` reference, not on every keystroke. */
  baseline: ReplayResult | null
  /** The effect of applying `operation` on top of `history`, without committing it. `null` while `operation` is `null`, a fetch is in flight, or the fetch failed (see `error`). */
  preview: ReplayResult | null
  loading: boolean
  /**
   * Set when the most recent baseline or preview fetch rejected (e.g. the
   * Worker call itself failed) -- `null` otherwise. Exists so a dialog's
   * preview panel can show a concrete failure state instead of spinning
   * on "Calculating preview…" forever, which is exactly the bug a rejected
   * `previewOperationAsync`/`currentActivityAsync` call used to cause
   * before this field existed (see this milestone's handoff report).
   */
  error: string | null
}

interface BaselineEntry {
  history: EditHistoryState
  result: ReplayResult | null
  error: string | null
}

interface PreviewEntry {
  key: string
  result: ReplayResult | null
  error: string | null
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * A content-based identity for an `Operation` -- every variant has only
 * primitive/`Date` fields, and `JSON.stringify` already serializes
 * `Date` via its `toISOString()`-equivalent `toJSON()`, so this is a
 * cheap, reliable way to tell "is this the same edit the user is
 * configuring" apart from mere object-reference identity. That
 * distinction matters here: callers deliberately rebuild a fresh
 * `Operation` object on every render (see each dialog's `buildOperation`)
 * so this hook's debounce reacts to real input changes, which means
 * comparing by reference would treat every render as a new operation.
 */
function operationKey(operation: Operation | null): string {
  return operation == null ? '' : JSON.stringify(operation)
}

/**
 * Drives the "live preview" requirement shared by every range-based edit
 * dialog: debounces `operation` changes, calls `previewOperationAsync`
 * off the main thread, and exposes a concrete before/after so the dialog
 * can show real numbers (record count, elapsed time) rather than asking
 * the user to "trust me". Never commits anything -- `history`'s log/cursor
 * are untouched by this hook.
 *
 * Deliberately avoids ever calling `setState` synchronously inside an
 * effect body (flagged by `react-hooks/set-state-in-effect` as a
 * cascading-render smell) -- `baseline`/`preview`/`loading` are all
 * derived during render by comparing the latest fetched entry's identity
 * against the current `history`/`operation`, rather than being reset
 * from inside the effect itself.
 */
export function useOperationPreview(
  history: EditHistoryState | undefined,
  operation: Operation | null,
): OperationPreviewState {
  const [baselineEntry, setBaselineEntry] = useState<BaselineEntry | null>(null)
  const [previewEntry, setPreviewEntry] = useState<PreviewEntry | null>(null)
  // Two independent counters -- baseline and preview are fetched
  // independently, so a stale *baseline* response must only ever be
  // discarded in favor of a newer *baseline* response, never because an
  // unrelated preview request happened to start more recently.
  const baselineRequestIdRef = useRef(0)
  const previewRequestIdRef = useRef(0)

  useEffect(() => {
    if (history == null) return
    const requestId = ++baselineRequestIdRef.current
    currentActivityAsync(history)
      .then((result) => {
        if (baselineRequestIdRef.current === requestId) {
          setBaselineEntry({ history, result, error: null })
        }
      })
      .catch((error: unknown) => {
        if (baselineRequestIdRef.current === requestId) {
          setBaselineEntry({ history, result: null, error: errorMessage(error) })
        }
      })
  }, [history])

  useEffect(() => {
    if (history == null || operation == null) return
    const requestId = ++previewRequestIdRef.current
    const key = operationKey(operation)
    const timer = setTimeout(() => {
      previewOperationAsync(history, operation)
        .then((result) => {
          if (previewRequestIdRef.current === requestId)
            setPreviewEntry({ key, result, error: null })
        })
        .catch((error: unknown) => {
          if (previewRequestIdRef.current === requestId) {
            setPreviewEntry({ key, result: null, error: errorMessage(error) })
          }
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [history, operation])

  const baselineMatches = baselineEntry != null && baselineEntry.history === history
  const baseline = baselineMatches ? baselineEntry.result : null
  const currentKey = operationKey(operation)
  const previewMatches =
    operation != null && previewEntry != null && previewEntry.key === currentKey
  const preview = previewMatches ? previewEntry.result : null
  const error =
    (previewMatches ? previewEntry.error : null) ?? (baselineMatches ? baselineEntry.error : null)
  const loading = operation != null && preview == null && error == null

  return { baseline, preview, loading, error }
}
